/**
 * VacationAccountService.js
 * Service layer for managing employee vacation accounts per year
 */

import { db } from '../firebase-config';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import VacationAccount, { VacationAdjustment } from '../models/VacationAccountModel';
import holidayService from './HolidayService';

class VacationAccountService {
  /**
   * Get Firestore reference for a vacation account
   */
  getAccountRef(companyId, staffId, year) {
    return doc(db, 'companies', companyId, 'staff', staffId, 'vacationAccounts', year.toString());
  }

  /**
   * Get a single vacation account for a specific year
   */
  async getAccount(companyId, staffId, year) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);
      
      if (accountDoc.exists()) {
        return VacationAccount.fromFirestore(accountDoc.data());
      }
      
      return null;
    } catch (error) {
      console.error('Error getting vacation account:', error);
      throw error;
    }
  }

  /**
   * Create a new vacation account for a year
   */
  async createAccount(companyId, staffId, year, data = {}) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      
      const existingAccount = await getDoc(accountRef);
      if (existingAccount.exists()) {
        throw new Error(`Vacation account for year ${year} already exists`);
      }

      const account = new VacationAccount({
        year: year.toString(),
        baseEntitlement: data.baseEntitlement || 0,
        carryOver: data.carryOver || 0,
        carryOverLimit: data.carryOverLimit || 0,
        carryOverDeadline: data.carryOverDeadline || null,
        adjustments: [],
        usedDays: 0
      });

      account.recalculate();

      await setDoc(accountRef, account.toFirestore());

      return account;
    } catch (error) {
      console.error('Error creating vacation account:', error);
      throw error;
    }
  }

  /**
   * Add an adjustment to a vacation account
   */
  async addAdjustment(companyId, staffId, year, adjustment) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (!accountDoc.exists()) {
        throw new Error(`Vacation account for year ${year} does not exist`);
      }

      const account = VacationAccount.fromFirestore(accountDoc.data());
      
      const newAdjustment = new VacationAdjustment(adjustment);
      
      account.adjustments.push(newAdjustment);
      
      account.recalculate();

      await updateDoc(accountRef, {
        adjustments: account.adjustments.map(adj => adj.toFirestore()),
        totalEntitlement: account.totalEntitlement,
        remainingDays: account.remainingDays,
        updatedAt: new Date()
      });

      return account;
    } catch (error) {
      console.error('Error adding adjustment:', error);
      throw error;
    }
  }

  /**
   * Delete a specific adjustment from a vacation account by its index
   * Removes the adjustment from the array, recalculates totals, and persists to Firestore
   * @param {string} companyId
   * @param {string} staffId
   * @param {string} year
   * @param {number} adjustmentIndex - The index of the adjustment to remove
   * @returns {Promise<VacationAccount>} The updated account after removal
   */
  async deleteAdjustment(companyId, staffId, year, adjustmentIndex) {
    try {
      if (!companyId || !staffId || !year) {
        throw new Error('Company ID, staff ID, and year are required');
      }

      if (typeof adjustmentIndex !== 'number' || adjustmentIndex < 0) {
        throw new Error('A valid adjustment index is required');
      }

      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (!accountDoc.exists()) {
        throw new Error(`Vacation account for year ${year} does not exist`);
      }

      const account = VacationAccount.fromFirestore(accountDoc.data());

      if (adjustmentIndex >= account.adjustments.length) {
        throw new Error(
          `Adjustment index ${adjustmentIndex} is out of range. Account has ${account.adjustments.length} adjustment(s)`
        );
      }

      // Remove the adjustment at the specified index
      account.adjustments.splice(adjustmentIndex, 1);

      // Recalculate totals with the adjustment removed
      account.recalculate();

      // Persist the updated adjustments array and recalculated totals
      await updateDoc(accountRef, {
        adjustments: account.adjustments.map(adj => adj.toFirestore()),
        totalEntitlement: account.totalEntitlement,
        remainingDays: account.remainingDays,
        updatedAt: new Date()
      });

      console.log(
        `Successfully deleted adjustment at index ${adjustmentIndex} for staff ${staffId} - year ${year}`
      );

      return account;
    } catch (error) {
      console.error('Error deleting adjustment:', error);
      throw new Error(`Failed to delete adjustment: ${error.message}`);
    }
  }

  /**
   * Recalculate account totals based on actual vacation usage
   */
  async recalculateAccount(companyId, staffId, year) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (!accountDoc.exists()) {
        throw new Error(`Vacation account for year ${year} does not exist`);
      }

      const account = VacationAccount.fromFirestore(accountDoc.data());

      const usedDays = await this.calculateUsedDays(companyId, staffId, year);

      account.recalculate(usedDays);

      await updateDoc(accountRef, {
        usedDays: account.usedDays,
        totalEntitlement: account.totalEntitlement,
        remainingDays: account.remainingDays,
        updatedAt: new Date()
      });

      return account;
    } catch (error) {
      console.error('Error recalculating account:', error);
      throw error;
    }
  }

  /**
   * Calculate used vacation days from the vacations collection for a specific year
   *
   * Business Logic:
   * 1. Only count vacations where durationMinutes > 0 (shift exists = counts as vacation day)
   * 2. Exclude public holidays (even if vacation exists on a holiday, it doesn't count)
   * 3. Each vacation document represents ONE day (not a date range)
   * 4. Vacations with durationMinutes = 0 mean no shift was scheduled, so they don't count
   *
   * Collection Path: companies/{companyId}/vacations/{vacationId}
   * Document ID Format: {staffId}_{YYYY-MM-DD}_{departmentId}
   */
  async calculateUsedDays(companyId, staffId, year) {
    try {
      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      
      const startDateString = `${year}-01-01`;
      const endDateString = `${year}-12-31`;

      console.log(`Calculating used vacation days for staff ${staffId} in year ${year}`);

      const q = query(
        vacationsRef,
        where('staffId', '==', staffId),
        where('date', '>=', startDateString),
        where('date', '<=', endDateString)
      );

      const querySnapshot = await getDocs(q);
      
      console.log(`Found ${querySnapshot.size} vacation documents for ${staffId} in ${year}`);

      let holidayDates = new Set();
      try {
        const holidays = await holidayService.fetchHolidaysForYear(companyId, parseInt(year));
        holidayDates = new Set(holidays.map(h => h.dateString));
        console.log(`Found ${holidayDates.size} public holidays in ${year}`);
      } catch (error) {
        console.warn('Could not fetch holidays for vacation calculation, continuing without holiday exclusion:', error.message);
      }

      let totalDays = 0;
      let skippedNoShift = 0;
      let skippedHoliday = 0;
      const countedVacations = [];
      const skippedVacations = [];
      
      querySnapshot.forEach((doc) => {
        const vacation = doc.data();
        const vacationInfo = {
          id: doc.id,
          date: vacation.date,
          durationMinutes: vacation.durationMinutes || 0,
          departmentId: vacation.departmentId
        };
        
        if (!vacation.durationMinutes || vacation.durationMinutes === 0) {
          skippedNoShift++;
          skippedVacations.push({ ...vacationInfo, reason: 'no_shift' });
          return;
        }
        
        if (holidayDates.has(vacation.date)) {
          skippedHoliday++;
          skippedVacations.push({ ...vacationInfo, reason: 'public_holiday' });
          return;
        }
        
        totalDays++;
        countedVacations.push(vacationInfo);
      });

      console.log(`Vacation calculation complete for ${staffId} in ${year}:
        Total vacation documents found: ${querySnapshot.size}
        Counted as vacation days: ${totalDays}
        Skipped (no shift scheduled): ${skippedNoShift}
        Skipped (public holiday): ${skippedHoliday}`);

      return totalDays;
    } catch (error) {
      console.error('Error calculating used vacation days:', error);
      throw error;
    }
  }

  /**
   * Update account settings (base entitlement, carryover limit, deadline, etc.)
   */
  async updateAccountSettings(companyId, staffId, year, fields) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (!accountDoc.exists()) {
        throw new Error(`Vacation account for year ${year} does not exist`);
      }

      const account = VacationAccount.fromFirestore(accountDoc.data());

      if (fields.baseEntitlement !== undefined) {
        account.baseEntitlement = fields.baseEntitlement;
      }
      if (fields.carryOver !== undefined) {
        account.carryOver = fields.carryOver;
      }
      if (fields.carryOverLimit !== undefined) {
        account.carryOverLimit = fields.carryOverLimit;
      }
      if (fields.carryOverDeadline !== undefined) {
        account.carryOverDeadline = fields.carryOverDeadline;
      }

      account.recalculate();

      await updateDoc(accountRef, {
        baseEntitlement: account.baseEntitlement,
        carryOver: account.carryOver,
        carryOverLimit: account.carryOverLimit,
        carryOverDeadline: account.carryOverDeadline,
        totalEntitlement: account.totalEntitlement,
        remainingDays: account.remainingDays,
        updatedAt: new Date()
      });

      return account;
    } catch (error) {
      console.error('Error updating account settings:', error);
      throw error;
    }
  }

  /**
   * Get all vacation accounts for a staff member
   */
  async getAllAccounts(companyId, staffId) {
    try {
      const accountsRef = collection(db, 'companies', companyId, 'staff', staffId, 'vacationAccounts');
      const querySnapshot = await getDocs(accountsRef);
      
      const accounts = [];
      querySnapshot.forEach((doc) => {
        accounts.push(VacationAccount.fromFirestore(doc.data()));
      });

      accounts.sort((a, b) => parseInt(b.year) - parseInt(a.year));

      return accounts;
    } catch (error) {
      console.error('Error getting all accounts:', error);
      throw error;
    }
  }

  /**
   * Check if a staff member has an account for a specific year
   */
  async accountExists(companyId, staffId, year) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);
      return accountDoc.exists();
    } catch (error) {
      console.error('Error checking account existence:', error);
      throw error;
    }
  }

  /**
   * Calculate carryover from previous year
   */
  async calculateCarryoverFromPreviousYear(companyId, staffId, year) {
    try {
      const previousYear = (parseInt(year) - 1).toString();
      const previousAccount = await this.getAccount(companyId, staffId, previousYear);

      if (!previousAccount) {
        return 0;
      }

      return Math.max(0, previousAccount.remainingDays);
    } catch (error) {
      console.error('Error calculating carryover:', error);
      return 0;
    }
  }

  /**
   * Initialize account with automatic carryover from previous year
   */
  async initializeAccountWithCarryover(companyId, staffId, year, baseEntitlement, carryOverLimit = 0) {
    try {
      const carryOver = await this.calculateCarryoverFromPreviousYear(companyId, staffId, year);
      
      return await this.createAccount(companyId, staffId, year, {
        baseEntitlement,
        carryOver: Math.min(carryOver, carryOverLimit),
        carryOverLimit
      });
    } catch (error) {
      console.error('Error initializing account with carryover:', error);
      throw error;
    }
  }

  /**
   * Expire carryover days after deadline (manual trigger by admin)
   */
  async expireCarryover(companyId, staffId, year) {
    try {
      const account = await this.getAccount(companyId, staffId, year);
      
      if (!account || !account.isCarryOverExpired()) {
        return account;
      }

      await this.addAdjustment(companyId, staffId, year, {
        type: 'carryover_correction',
        days: -account.carryOver,
        note: `Carryover expired after ${account.carryOverDeadline.toLocaleDateString()}`,
        createdAt: new Date(),
        createdBy: 'system'
      });

      return await this.updateAccountSettings(companyId, staffId, year, {
        carryOver: 0
      });
    } catch (error) {
      console.error('Error expiring carryover:', error);
      throw error;
    }
  }

  /**
   * Delete a vacation account for a specific year
   * WARNING: This will permanently delete all account data including adjustments
   * @param {string} companyId
   * @param {string} staffId
   * @param {string} year - Year as string (e.g., "2025")
   * @returns {Promise<void>}
   */
  async deleteVacationAccount(companyId, staffId, year) {
    try {
      if (!companyId || !staffId || !year) {
        throw new Error('Company ID, staff ID, and year are required');
      }

      const accountRef = doc(
        db,
        'companies',
        companyId,
        'staff',
        staffId,
        'vacationAccounts',
        year
      );

      const accountDoc = await getDoc(accountRef);
      if (!accountDoc.exists()) {
        throw new Error(`No vacation account found for year ${year}`);
      }

      await deleteDoc(accountRef);

      console.log(`Successfully deleted vacation account for ${staffId} - year ${year}`);
    } catch (error) {
      console.error('Error deleting vacation account:', error);
      throw new Error(`Failed to delete vacation account: ${error.message}`);
    }
  }
}

export default new VacationAccountService();